import {
	Box,
	Button,
	Checkbox,
	Container,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Heading,
	Icon,
	Input,
	Link,
	Text,
	VStack,
} from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Redirect, useLocation } from "react-router-dom";
import {
	clearVerificationState,
	handleLogin,
	handleResendVerification,
	handleVerifyLogin,
} from "../redux/slices/authSlice";
import { useAppDispatch, useAppSelector } from "../utils/hook";
// images
import { FiLock, FiUser } from "react-icons/fi";
import OTPInput from "../components/OTPInput";
import { validationMsg } from "../data";
import bg from "../images/login-bg.webp";
import { emailRegex } from "../utils/helpers";

interface Props {}
const Login: FC<Props> = () => {
	const { state } = useLocation<any>();
	const isAuthed = useAppSelector((state) => state.auth.isAuthed);

	if (isAuthed) {
		return <Redirect to={state?.from ? state.from : "/"} />;
	}

	return (
		<Flex
			className="page"
			py="0"
			alignItems="stretch"
			sx={{
				"> div": {
					pos: "relative",
					minH: "100vh",
					flex: "1 1 50%",
				},
			}}
		>
			<Box
				d={{ base: "none", md: "block" }}
				bgImage={`url(${bg})`}
				bgPos="center"
				bgSize="cover"
				bgRepeat="no-repeat"
			/>
			<Flex direction="column" justify="space-evenly">
				<Container>
					<Heading textAlign="center" letterSpacing="widest">
						BARQ
					</Heading>
				</Container>
				<Container maxW="md" mt="5">
					<Heading size="md" mb="5">
						لوحة التحكم
					</Heading>
					<LoginFlow />
				</Container>
			</Flex>
		</Flex>
	);
};

const LoginFlow: FC = () => {
	const pendingVerification = useAppSelector(
		(state) => state.auth.pendingVerification
	);

	return pendingVerification ? <OTPForm /> : <CredentialsForm />;
};

const CredentialsForm: FC = () => {
	const dispatch = useAppDispatch();
	const isLoading = useAppSelector((state) => state.auth.isLoading);
	const {
		register,
		formState: { errors },
		handleSubmit,
	} = useForm<LoginData>();

	function onSubmit(data: LoginData) {
		dispatch(handleLogin(data));
	}

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			noValidate
			style={{ textAlign: "center" }}
		>
			<VStack
				alignItems="start"
				mb="5"
				sx={{
					".chakra-form-control input": {
						ps: "6",
					},
					".chakra-form-control .chakra-icon": {
						pos: "absolute",
						top: "50%",
						transform: "translateY(-50%)",
					},
				}}
			>
				<FormControl isInvalid={!!errors.email}>
					<FormLabel srOnly>Email Address</FormLabel>
					<Box pos="relative">
						<Icon as={FiUser} />
						<Input
							type="email"
							placeholder="البريد الإلكتروني"
							{...register("email", {
								required: validationMsg["required"],
								pattern: {
									value: emailRegex,
									message: validationMsg["invalidEmail"],
								},
							})}
							autoFocus
						/>
					</Box>
					<FormErrorMessage>{errors.email?.message}</FormErrorMessage>
				</FormControl>
				<FormControl isInvalid={!!errors.password}>
					<FormLabel srOnly>Password</FormLabel>
					<Box pos="relative">
						<Icon as={FiLock} />
						<Input
							type="password"
							placeholder="كلمة المرور"
							{...register("password", {
								required: validationMsg["required"],
							})}
						/>
					</Box>
					<FormErrorMessage>{errors.password?.message}</FormErrorMessage>
				</FormControl>
				<Checkbox>تذكرني</Checkbox>
			</VStack>
			<Button
				isLoading={isLoading}
				type="submit"
				minW="80%"
				borderRadius="full"
			>
				تسجيل الدخول
			</Button>
		</form>
	);
};

const OTPForm: FC = () => {
	const dispatch = useAppDispatch();
	const isLoading = useAppSelector((state) => state.auth.isLoading);
	const loginCredentials = useAppSelector(
		(state) => state.auth.loginCredentials
	);
	const expiresInMinutes = useAppSelector(
		(state) => state.auth.expiresInMinutes
	);
	const verificationEmail = useAppSelector(
		(state) => state.auth.verificationEmail
	);
	const [otpValue, setOtpValue] = useState("");
	const [countdown, setCountdown] = useState(
		expiresInMinutes ? expiresInMinutes * 60 : 600
	);
	const [canResend, setCanResend] = useState(false);

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
			return () => clearTimeout(timer);
		} else {
			setCanResend(true);
		}
	}, [countdown]);

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	const handleOTPSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (otpValue.length === 6 && loginCredentials?.email) {
			dispatch(
				handleVerifyLogin({
					email: loginCredentials.email,
					verification_code: otpValue,
				})
			);
		}
	};

	const handleResend = async () => {
		if (canResend) {
			await dispatch(handleResendVerification());
			setCountdown(expiresInMinutes ? expiresInMinutes * 60 : 600);
			setCanResend(false);
		}
	};

	const handleBack = () => {
		dispatch(clearVerificationState());
		setOtpValue("");
	};

	return (
		<form onSubmit={handleOTPSubmit} style={{ textAlign: "center" }}>
			<VStack spacing="5" mb="5">
				<Box>
					<Heading size="sm" mb="2">
						رمز التحقق
					</Heading>
					<Text fontSize="sm" color="gray.600">
						تم إرسال رمز التحقق إلى بريدك الإلكتروني
					</Text>
					<Text fontSize="sm" color="gray.600" fontWeight="bold">
						{verificationEmail}
					</Text>
				</Box>

				<FormControl>
					<OTPInput
						value={otpValue}
						onChange={setOtpValue}
						isDisabled={isLoading}
					/>
				</FormControl>

				<Box>
					<Text fontSize="sm" color="gray.600">
						{canResend ? (
							<Link
								color="blue.500"
								cursor="pointer"
								onClick={handleResend}
								textDecoration="underline"
							>
								إعادة إرسال الرمز
							</Link>
						) : (
							<>
								يمكنك إعادة إرسال الرمز بعد{" "}
								<Text as="span" fontWeight="bold">
									{formatTime(countdown)}
								</Text>
							</>
						)}
					</Text>
				</Box>
			</VStack>

			<VStack spacing="3">
				<Button
					isLoading={isLoading}
					type="submit"
					minW="80%"
					borderRadius="full"
					isDisabled={otpValue.length !== 6}
				>
					تأكيد
				</Button>
				<Button
					variant="ghost"
					minW="80%"
					borderRadius="full"
					onClick={handleBack}
					isDisabled={isLoading}
				>
					رجوع
				</Button>
			</VStack>
		</form>
	);
};

export default Login;
